import React from 'react';
import { Card, CardContent, Typography, Box, Rating, Chip, Skeleton, useTheme } from '@mui/material';

const TAG_CATEGORIES = {
  tone:   ['Dark', 'Funny', 'Intense', 'Gritty', 'Cozy'],
  genre:  ['Mystery', 'Thriller', 'Crime', 'Procedural', 'Psychological'],
  pacing: ['Slow-burn', 'Fast-paced', 'Character-driven', 'Plot-heavy'],
  era:    ['Period piece', 'Contemporary', 'Historical'],
};

function getTagCategory(tag) {
  for (const [cat, tags] of Object.entries(TAG_CATEGORIES)) {
    if (tags.includes(tag)) return cat;
  }
  return 'other';
}

const TAG_COLORS = {
  tone:   { bg: 'rgba(249,115,22,0.15)',  color: '#fdba74' },
  genre:  { bg: 'rgba(232,184,109,0.18)', color: '#e8c07a' },
  pacing: { bg: 'rgba(52,211,153,0.15)',  color: '#6ee7b7' },
  era:    { bg: 'rgba(167,139,250,0.15)', color: '#c4b5fd' },
  other:  { bg: 'rgba(255,255,255,0.08)', color: '#cbd5e1' },
};

const CARD_BG = '#1c1810';

export default function ShowCard({ show, onClick, hideCTA = false, scoreLabel = null }) {
  const theme = useTheme();
  const accentColor = theme.palette.primary.main;

  return (
    <Card
      onClick={onClick}
      sx={{
        display: 'flex',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 28px rgba(0,0,0,0.5), 0 0 0 1px ${accentColor}28`,
        } : {},
      }}
    >
      {/* Poster */}
      <Box sx={{ position: 'relative', flexShrink: 0, width: 90 }}>
        {(() => {
          const isPlaceholder = !show.poster_path || show.poster_path.includes('placeholder');
          if (isPlaceholder && !show.posterUrl) {
            return (
              <Skeleton
                variant="rectangular"
                width={90}
                height={135}
                sx={{ bgcolor: 'grey.900', flexShrink: 0 }}
              />
            );
          }
          const src = (() => {
            if (show.poster_path) {
              if (show.poster_path.startsWith('http')) return show.poster_path;
              if (show.poster_path.includes('placeholder')) return show.posterUrl || '/poster_placeholder.svg';
              return `https://image.tmdb.org/t/p/w300${show.poster_path}`;
            }
            return show.posterUrl || '/poster_placeholder.svg';
          })();
          return (
            <Box
              component="img"
              src={src}
              alt={show.title}
              sx={{ width: 90, height: 135, objectFit: 'cover', display: 'block', bgcolor: 'grey.900' }}
              onError={e => {
                e.target.onerror = null;
                e.target.src = '/poster_placeholder.svg';
                e.target.style.opacity = '0.5';
              }}
            />
          );
        })()}
        <Box sx={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: 20,
          background: `linear-gradient(to right, transparent, ${CARD_BG})`,
        }} />
      </Box>

      {/* Content */}
      <CardContent sx={{ flex: 1, py: 1.5, pl: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', lineHeight: 1.3, mb: 0.25 }}>
          {show.title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {show.year}{show.seasons ? ` · ${show.seasons} season${show.seasons !== 1 ? 's' : ''}` : ''}
        </Typography>

        {!hideCTA && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {show.rating > 0 ? (
              <Rating
                value={show.rating}
                precision={0.5}
                readOnly
                size="small"
                sx={{ '& .MuiRating-iconFilled': { color: accentColor } }}
              />
            ) : (
              <Typography
                variant="caption"
                sx={{
                  color: accentColor,
                  fontWeight: 600,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  opacity: 0.8,
                }}
              >
                Seen it? Rate it
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {scoreLabel != null && (
            <Chip
              label={`${scoreLabel}/10`}
              size="small"
              sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(232,184,109,0.18)', color: '#e8c07a', border: 'none', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
            />
          )}
          {(show.tags || []).map(tag => {
            const c = TAG_COLORS[getTagCategory(tag)];
            return (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{ height: 20, fontSize: '0.68rem', bgcolor: c.bg, color: c.color, border: 'none', fontFamily: '"Inter", sans-serif' }}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
